import { useCallback, useEffect, useState } from "react";
import { api, extractAxiosError } from "@/lib/api";
import { AdminPanelShell } from "@/components/admin/AdminPanelShell";
import { Loader2, Mail, RefreshCw, Send, X } from "lucide-react";
import { toast } from "sonner";

const FROM_PROFILE_LABELS = {
    noreply: "No-reply (noreply@)",
    support: "Support (support@)",
    billing: "Billing (billing@)",
    security: "Security (security@)",
    contact: "Contact (contact@)",
};

function formatWhen(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch (_e) {
        return iso;
    }
}

function statusClass(status) {
    const value = (status || "").toLowerCase();
    if (value.includes("error") || value === "failed") return "text-rose-400";
    if (value === "sent" || value === "delivered") return "text-emerald-400";
    if (value === "logged") return "text-amber-300";
    return "text-white/55";
}

function EmailDetailDrawer({ logId, onClose }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!logId) return;
        let mounted = true;
        setLoading(true);
        api.get(`/admin/email/logs/${logId}`)
            .then(({ data }) => { if (mounted) setDetail(data); })
            .catch((e) => toast.error(extractAxiosError(e, "Could not load email")))
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [logId]);

    if (!logId) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50" data-testid="admin-email-detail">
            <button type="button" className="flex-1" aria-label="Close email detail" onClick={onClose} />
            <aside className="flex h-full w-full max-w-xl flex-col border-l border-white/10 bg-[#1A1A1A] shadow-2xl">
                <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-4">
                    <h3 className="font-heading text-lg">Email detail</h3>
                    <button type="button" onClick={onClose} className="btn-ghost rounded-md p-2">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                    {loading ? (
                        <div className="grid h-40 place-items-center text-sm text-white/45">Loading…</div>
                    ) : detail ? (
                        <div className="space-y-4 text-sm">
                            <div className="grid gap-2">
                                <p><span className="text-white/45">To:</span> {detail.to}</p>
                                <p><span className="text-white/45">Subject:</span> {detail.subject}</p>
                                <p><span className="text-white/45">Status:</span> <span className={statusClass(detail.status)}>{detail.status || "—"}</span></p>
                                <p><span className="text-white/45">Provider:</span> {detail.provider || "—"}</p>
                                <p><span className="text-white/45">Template:</span> {detail.template || "—"}</p>
                                <p><span className="text-white/45">Sent:</span> {formatWhen(detail.created_at)}</p>
                                {detail.from_header && <p><span className="text-white/45">From:</span> {detail.from_header}</p>}
                                {detail.error && <p className="text-rose-400">Error: {detail.error}</p>}
                            </div>
                            {detail.text && (
                                <div>
                                    <p className="label-eyebrow mb-2">Plain text</p>
                                    <pre className="whitespace-pre-wrap rounded-lg border border-white/[0.06] bg-black/20 p-3 text-xs text-white/75">{detail.text}</pre>
                                </div>
                            )}
                            {detail.html && (
                                <div>
                                    <p className="label-eyebrow mb-2">HTML preview</p>
                                    <iframe
                                        title="Email HTML preview"
                                        sandbox=""
                                        srcDoc={detail.html}
                                        className="h-80 w-full rounded-lg border border-white/[0.06] bg-white"
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-white/45">Email not found.</p>
                    )}
                </div>
            </aside>
        </div>
    );
}

export function AdminEmailsPage() {
    const [tab, setTab] = useState("logs");
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ search: "", to: "", status: "", template: "" });
    const [selectedLogId, setSelectedLogId] = useState(null);
    const [compose, setCompose] = useState({ to: "", subject: "", message: "", from_profile: "support" });
    const [sending, setSending] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/email/status");
            setStatus(data);
        } catch (_e) {
            setStatus(null);
        }
    }, []);

    const loadLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (filters.search.trim()) params.search = filters.search.trim();
            if (filters.to.trim()) params.to = filters.to.trim();
            if (filters.status) params.status = filters.status;
            if (filters.template.trim()) params.template = filters.template.trim();
            const { data } = await api.get("/admin/email/logs", { params });
            setLogs(data.logs || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load email logs"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { loadStatus(); }, [loadStatus]);
    useEffect(() => { loadLogs(); }, [loadLogs]);

    const selectedProfile = status?.from_profiles?.[compose.from_profile];

    const sendEmail = async (e) => {
        e.preventDefault();
        if (!compose.to.trim() || !compose.subject.trim() || !compose.message.trim()) {
            toast.error("To, subject, and message are required");
            return;
        }
        setSending(true);
        try {
            await api.post("/admin/email/send", {
                to: compose.to.trim(),
                subject: compose.subject.trim(),
                message: compose.message,
                from_profile: compose.from_profile,
            });
            toast.success("Email sent");
            setCompose((c) => ({ ...c, subject: "", message: "" }));
            loadLogs();
            setTab("logs");
        } catch (err) {
            toast.error(extractAxiosError(err, "Could not send email"));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-emails-page">
            {status && (
                <div className="surface p-4 text-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="label-eyebrow inline-flex items-center gap-1.5">
                                <Mail className="admin-accent-icon h-3.5 w-3.5" /> Email delivery
                            </p>
                            <p className="mt-1 text-white/70">
                                Provider: <span className="font-mono text-white">{status.provider || "—"}</span>
                                {" · "}
                                {status.configured ? "Resend configured" : "Console-only (no Resend key)"}
                            </p>
                            <p className="mt-1 text-xs text-white/45">Default from: {status.from || "—"}</p>
                        </div>
                        {(status.warnings || []).length > 0 && (
                            <ul className="max-w-md space-y-1 text-xs text-amber-200/90">
                                {status.warnings.map((w) => <li key={w}>• {w}</li>)}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            <div className="flex gap-2 border-b border-white/[0.06] pb-1">
                {[
                    { id: "logs", label: "Email log" },
                    { id: "compose", label: "Send email" },
                ].map(({ id, label }) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => setTab(id)}
                        className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                            tab === id ? "bg-white/[0.08] text-white" : "text-white/55 hover:text-white"
                        }`}
                        data-testid={`admin-email-tab-${id}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {tab === "logs" && (
                <>
                    <div className="surface grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <label className="label-eyebrow mb-1.5 block">Search</label>
                            <input
                                value={filters.search}
                                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                                placeholder="To, subject, or ID"
                                className="input-dark w-full rounded-md px-3 py-2 text-sm"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-1.5 block">Recipient</label>
                            <input
                                value={filters.to}
                                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                                placeholder="Filter by email"
                                className="input-dark w-full rounded-md px-3 py-2 text-sm"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-1.5 block">Status</label>
                            <input
                                value={filters.status}
                                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                                placeholder="e.g. sent, logged, error"
                                className="input-dark w-full rounded-md px-3 py-2 text-sm"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-1.5 block">Template</label>
                            <input
                                value={filters.template}
                                onChange={(e) => setFilters((f) => ({ ...f, template: e.target.value }))}
                                placeholder="e.g. admin_compose"
                                className="input-dark w-full rounded-md px-3 py-2 text-sm"
                            />
                        </div>
                    </div>

                    <AdminPanelShell
                        title="All outbound emails"
                        count={total}
                        loading={loading}
                        empty={!loading && logs.length === 0}
                        emptyMessage="No emails logged yet."
                        testId="admin-email-logs"
                        headerExtra={(
                            <button type="button" onClick={loadLogs} className="btn-ghost inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs">
                                <RefreshCw className="h-3.5 w-3.5" /> Refresh
                            </button>
                        )}
                    >
                        <table className="w-full min-w-[720px] text-left text-sm">
                            <thead className="sticky top-0 z-10 bg-[#262626] text-xs uppercase tracking-wide text-white/40">
                                <tr>
                                    <th className="px-4 py-3 font-medium">When</th>
                                    <th className="px-4 py-3 font-medium">To</th>
                                    <th className="px-4 py-3 font-medium">Subject</th>
                                    <th className="px-4 py-3 font-medium">Template</th>
                                    <th className="px-4 py-3 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {logs.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="cursor-pointer hover:bg-white/[0.03]"
                                        onClick={() => setSelectedLogId(row.id)}
                                        data-testid={`admin-email-row-${row.id}`}
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap text-white/55">{formatWhen(row.created_at)}</td>
                                        <td className="px-4 py-3 max-w-[180px] truncate">{row.to}</td>
                                        <td className="px-4 py-3 max-w-[240px] truncate">{row.subject}</td>
                                        <td className="px-4 py-3 text-white/45">{row.template || "—"}</td>
                                        <td className={`px-4 py-3 font-mono text-xs ${statusClass(row.status)}`}>{row.status || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </AdminPanelShell>
                </>
            )}

            {tab === "compose" && (
                <form onSubmit={sendEmail} className="surface max-w-2xl space-y-4 p-6" data-testid="admin-email-compose">
                    <div>
                        <p className="font-heading text-xl">Send email</p>
                        <p className="mt-1 text-sm text-white/50">
                            Compose and send a branded FairShare email using verified outbound addresses.
                        </p>
                    </div>
                    <label className="block">
                        <span className="label-eyebrow">Send from</span>
                        <select
                            value={compose.from_profile}
                            onChange={(e) => setCompose((c) => ({ ...c, from_profile: e.target.value }))}
                            className="input-dark mt-2 w-full rounded-md px-3 py-2 text-sm"
                        >
                            {(status?.from_profiles
                                ? Object.entries(status.from_profiles).map(([key, profile]) => (
                                    <option key={key} value={key}>{profile.from}</option>
                                ))
                                : Object.entries(FROM_PROFILE_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))
                            )}
                        </select>
                        {selectedProfile && (
                            <p className="mt-2 text-xs text-white/45">
                                Recipients will see <span className="font-mono text-white/65">{selectedProfile.from}</span>
                                {selectedProfile.reply_to && selectedProfile.reply_to !== selectedProfile.from
                                    ? <> · replies go to <span className="font-mono text-white/65">{selectedProfile.reply_to}</span></>
                                    : null}
                            </p>
                        )}
                        <p className="mt-2 text-xs text-white/40">
                            Pick the alias, fill in To / Subject / Message, then Send. Each alias maps to a verified address on your domain in Resend (e.g. support@, billing@).
                        </p>
                    </label>
                    <label className="block">
                        <span className="label-eyebrow">To</span>
                        <input
                            type="email"
                            required
                            value={compose.to}
                            onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
                            className="input-dark mt-2 w-full rounded-md px-3 py-2 text-sm"
                            placeholder="recipient@example.com"
                        />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow">Subject</span>
                        <input
                            required
                            value={compose.subject}
                            onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
                            className="input-dark mt-2 w-full rounded-md px-3 py-2 text-sm"
                            placeholder="Subject line"
                        />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow">Message</span>
                        <textarea
                            required
                            rows={8}
                            value={compose.message}
                            onChange={(e) => setCompose((c) => ({ ...c, message: e.target.value }))}
                            className="input-dark mt-2 w-full rounded-md px-3 py-2 text-sm"
                            placeholder="Write your message. Use blank lines between paragraphs."
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={sending}
                        className="btn-neon inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm disabled:opacity-50"
                        data-testid="admin-email-send"
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {sending ? "Sending…" : "Send email"}
                    </button>
                </form>
            )}

            <EmailDetailDrawer logId={selectedLogId} onClose={() => setSelectedLogId(null)} />
        </div>
    );
}
